import React, { useState } from 'react';
import {
  type BlockItemRenderers,
  blockRenderers,
  CustomComponentRenderer,
  WebForm,
  SearchBox,
  CForm,
} from '@seek/cmsu-components';
import { useTranslations } from '@vocab/react';
import type { FormFragment, WebFormFragment } from '@seek/cmsu-cms-connect';
import { useFetchSuggestions } from 'src/client/shared/hooks/useFetchSuggestions';
import { useConfig } from 'src/client/shared/hooks';
import { LatestArticlesByCategoryLoader } from '../client/components/modules/LatestArticlesByCategory/LatestArticlesByCategoryLoader';
import { WPT_SALESFORCE_API } from '../config';
import { getPrivacyLink } from './form';
import translations from '../client/.vocab';
import { convertMelwaysLocale } from './convertMelwaysLocale';
import { useMelwaysLink } from '@seek/melways-react';

type WebFormProps = Pick<
  WebFormFragment,
  'formType' | 'document' | 'confirmationMessage' | 'webFormColumns'
>;

export const pageRenderers: BlockItemRenderers = {
  ...blockRenderers.hirer,
  CCustomComponent: ({ data }, additionalData) => {
    const { t } = useTranslations(translations);
    const [searchInput, setSearchInput] = useState<string | undefined>();
    const {
      country,
      language,
      melwaysLocale,
      sourceName,
      privacyUrl,
      recaptchaKey,
      section,
    } = useConfig();
    const locale = convertMelwaysLocale(melwaysLocale);
    const fetchedSuggestions = useFetchSuggestions(
      locale,
      searchInput,
      section,
    );
    const urlResolver = useMelwaysLink();

    return data ? (
      <CustomComponentRenderer
        data={data}
        renderers={{
          ...blockRenderers.customComponentRenderers,
          SearchBox: () => (
            <SearchBox
              placeholderText={t('Search box suggestion')}
              searchButtonText={t('Search button text')}
              onSearch={(term) => {
                window.location.href = urlResolver({
                  language,
                  path: `/article-search/${encodeURIComponent(
                    term,
                  )}/1?section=${section}`,
                });
              }}
              minChars={3}
              minCharsErrorMessage={t('Minimum chars error message', {
                minChars: '3',
              })}
              onKeyPress={setSearchInput}
              onSelect={(path) => {
                window.location.href = path;
              }}
              titleSuggestions={fetchedSuggestions}
            />
          ),
          WebForm: ({
            document,
            formType,
            confirmationMessage,
            webFormColumns,
          }: WebFormProps) => (
            <WebForm
              country={country}
              locale={melwaysLocale}
              privacyUrl={privacyUrl}
              sourceName={sourceName}
              recaptchaKey={recaptchaKey}
              columns={webFormColumns}
              formType={formType}
              confirmationMessage={confirmationMessage?.raw}
              document={document?.url}
              wptSalesForceApi={WPT_SALESFORCE_API}
              privacyLink={getPrivacyLink(melwaysLocale)}
            />
          ),
          CForm: ({ form }) => (
            <CForm
              locale={melwaysLocale}
              sourceName={sourceName}
              privacyUrl={privacyUrl}
              recaptchaKey={recaptchaKey}
              wptSalesForceApi={WPT_SALESFORCE_API}
              country={country}
              form={form as FormFragment}
            />
          ),
        }}
        additionalData={additionalData}
      />
    ) : null;
  },
  LatestArticlesByCategory: ({ category }) => (
    <LatestArticlesByCategoryLoader
      categoryName={category?.name}
      categorySlug={category?.slug}
    />
  ),
};
